import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { Radio } from '@/components/base/Radio'
import { SliderInput } from '@/pages/instance-list/components/SliderInput'
import { formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

export interface DurationOption {
  id: string
  months: number
  label: string
}

interface TeamDurationModalProps {
  open: boolean
  onClose: () => void
  onBack: () => void
  onConfirm: (duration: DurationOption) => void
  loading?: boolean
  pricePerMonth: number // cents, from selected plan
  step?: { current: number; total: number } // Custom step indicator, default { current: 3, total: 4 }
  titleKey?: string // Custom title i18n key, default 'createTeam.step3Title'
}

// Duration option config without label (will be added with i18n)
interface DurationOptionConfig {
  id: string
  months: number
  labelKey: string
}

// Static config for duration options
const DURATION_OPTIONS_CONFIG: DurationOptionConfig[] = [
  { id: '1m', months: 1, labelKey: 'duration.1month' },
  { id: '2m', months: 2, labelKey: 'duration.2months' },
  { id: '3m', months: 3, labelKey: 'duration.3months' },
  { id: '6m', months: 6, labelKey: 'duration.6months' },
  { id: '12m', months: 12, labelKey: 'duration.12months' },
  { id: 'custom', months: 0, labelKey: 'duration.custom' },
]

export function TeamDurationModal({
  open,
  onClose,
  onBack,
  onConfirm,
  loading = false,
  pricePerMonth,
  step = { current: 3, total: 4 },
  titleKey = 'createTeam.step3Title',
}: TeamDurationModalProps) {
  const { t } = useTranslation('team')
  const [selectedDuration, setSelectedDuration] = useState<string>('1m')
  const [customMonths, setCustomMonths] = useState(6) // default slider value

  // Generate duration options with translated labels
  const durationOptions: DurationOption[] = useMemo(
    () =>
      DURATION_OPTIONS_CONFIG.map((config) => ({
        id: config.id,
        months: config.months,
        label: t(config.labelKey),
      })),
    [t],
  )

  const getSelectedOption = (): DurationOption => {
    if (selectedDuration === 'custom') {
      return {
        id: 'custom',
        months: customMonths,
        label: t('duration.monthLabel', { count: customMonths }),
      }
    }
    return durationOptions.find((opt) => opt.id === selectedDuration)!
  }

  const selectedOption = getSelectedOption()

  const handleConfirm = () => {
    onConfirm(selectedOption)
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[800px] h-[600px]">
      <Modal.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-3 hover:bg-panel-gray-200 rounded">
              <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
            </button>
            <span className="h6">
              <Trans ns="team" i18nKey={titleKey}>
                Team Period
              </Trans>
            </span>
            <span className="h6 text-tertiary">
              <Trans
                ns="team"
                i18nKey="createTeam.stepIndicator"
                values={{ current: step.current, total: step.total }}
              >
                ({step.current}/{step.total})
              </Trans>
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="flex-1 overflow-y-auto">
        <div className="space-y-6">
          {/* Select Duration */}
          <div>
            <h3 className="t-sb mb-4 text-primary">{t('duration.selectTitle')}</h3>
            <div className="grid grid-cols-3 gap-4">
              {durationOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => setSelectedDuration(option.id)}
                  className={cn(
                    'w-full px-4 py-3 rounded-lg border border-neutral-200 hover:border-secondary transition-colors duration-300 cursor-pointer',
                    selectedDuration === option.id && 'border-secondary bg-btn-secondary/5',
                  )}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="text-primary">
                      <div className="t-bold">{option.label}</div>
                      <div className="t-x-sb mt-1">
                        {option.id === 'custom' ? (
                          <div className="text-tertiary">{t('duration.customHint')}</div>
                        ) : (
                          formatPrice(pricePerMonth * option.months)
                        )}
                      </div>
                    </div>
                    <Radio checked={option.id === selectedDuration} />
                  </div>
                </div>
              ))}
            </div>

            {/* Slider for Custom Duration */}
            {selectedDuration === 'custom' && (
              <div className="mt-4 p-4 border border-gray300 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 mb-4 text-primary">
                  <span className="t-bold bg-neutral-100 px-2 rounded">
                    {customMonths}{' '}
                    <span className="text-tertiary ml-1">{t('duration.monthUnit')}</span>
                  </span>
                  <span className="t-x-sb">{formatPrice(pricePerMonth * customMonths)}</span>
                </div>
                <SliderInput
                  value={customMonths}
                  min={1}
                  max={12}
                  step={1}
                  unit={t('duration.monthUnit')}
                  showReset={false}
                  showInput={false}
                  onChange={setCustomMonths}
                />
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-between w-full">
          <span className="h4 text-highlight">
            {formatPrice(pricePerMonth * selectedOption.months)}
          </span>
          <div className="flex gap-4">
            <Button type="outline" onClick={onBack}>
              {t('common.previous')}
            </Button>
            <Button type="secondary" onClick={handleConfirm} disabled={loading}>
              {loading ? t('common.loading') : t('common.next')}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
